import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import LoginPage from './pages/LoginPage';
import StatsPage from './pages/StatsPage';
import DashboardLayout from './layouts/DashboardLayout';
// NOT lazy, like every page below that is routed inside <ErrorBoundary> without
// <Suspense>: the home screen, the section front pages and the page-not-found screen
// are where people land, and a lazy page there renders blank in production.
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import SectionFrontPage from './pages/SectionFrontPage';
import NotFoundPage from './pages/NotFoundPage';
import SearchResultPage from './pages/SearchResultPage';
import MessagesPage from './pages/MessagesPage';
import EmployeeMasterPage from './pages/EmployeeMasterPage';
import StoreMasterPage from './pages/StoreMasterPage';
// Not lazy(): master-management routes are wrapped in ErrorBoundary but NOT
// Suspense, so a lazy page here renders blank in production.
import ProductMasterPage from './pages/ProductMasterPage';
import SalesTargetsPage from './pages/SalesTargetsPage';
import MyApprovalsPage from './pages/MyApprovalsPage';
import OpenProjectManagementPage from './pages/OpenProjectManagementPage';
import ProbationConfirmationPage from './pages/ProbationConfirmationPage';
import TasksPage from './pages/TasksPage';
import StoreVisitFormsPage from './pages/StoreVisitFormsPage';
import StoreVisitTasksReportPage from './pages/StoreVisitTasksReportPage';
// Lazy-loaded so the task report code is split out of the initial bundle
const TaskResponseListPage = lazy(() => import('./pages/TaskResponseListPage'));
const TaskResponseDetailPage = lazy(() => import('./pages/TaskResponseDetailPage'));
import GroomingReportPage from './pages/GroomingReportPage';
import MoodReportPage from './pages/MoodReportPage';
import StoreVisitsReportPage from './pages/StoreVisitsReportPage';
import RunningStockReportPage from './pages/RunningStockReportPage';
import { MobileUsageReportPage } from './pages/MobileUsageReportPage';
import QuizPerformanceReportPage from './pages/QuizPerformanceReportPage';
import ScormProgressReportPage from './pages/ScormProgressReportPage';
import LmsJourneyReportPage from './pages/LmsJourneyReportPage';
import LmsModuleReportPage, { LmsModuleLearnersRedirect } from './pages/LmsModuleReportPage';
const AppealWorkflowReportPage = lazy(() => import('./pages/AppealWorkflowReportPage'));
const YellowFlagAppealsPage = lazy(() => import('./pages/YellowFlagAppealsPage'));
const YellowFlagsReportPage = lazy(() => import('./pages/YellowFlagsReportPage'));
const PipPage = lazy(() => import('./pages/PipPage'));
const StartExitPage = lazy(() => import('./pages/StartExitPage'));
import UserRoleAssignmentPage from './pages/UserRoleAssignmentPage';
import DailyAttendanceLogsPage from './pages/DailyAttendanceLogsPage';
import RandomAttendanceLogsPage from './pages/RandomAttendanceLogsPage';
import LeavePolicyPage from './pages/LeavePolicyPage';
import AccessManagementPage from './pages/AccessManagementPage';
import ProjectManagementPage from './pages/ProjectManagementPage';
import MailerSetupPage from './pages/MailerSetupPage';
import DistributionListsPage from './pages/DistributionListsPage';
import MarketRegularizationReasonsPage from './pages/MarketRegularizationReasonsPage';
import GroomingPromptPage from './pages/GroomingPromptPage';
import YellowFlagReasonsPage from './pages/YellowFlagReasonsPage';
// Note: TargetsPage and AppleScorecardPage are imported indirectly via
// EmployeeScorePage's Manage tabs (embedded mode); they no longer have their
// own routes. EmployeeQuadrantsPage was retired in Phase 3 — its functionality
// lives on EmployeeScorePage's Quadrant View tab.
import EmployeeScorePage from './pages/EmployeeScorePage';
// NOT lazy: the /master-management/kpi route below wraps this in <ErrorBoundary>
// but NOT <Suspense>, matching its sibling master-management routes. A lazy()
// component with no Suspense boundary throws on render in a production build (it
// suspends on the network fetch of its chunk) — which renders a blank page that
// the ErrorBoundary swallows. A normal import has nothing to suspend on.
import KpiPage from './pages/KpiPage';
import { useRedFlagRulesEnabled } from './components/red-flag-rules/useRedFlagRulesEnabled';
// Lazy WITH its own <Suspense> (see RedFlagRulesRoute), so its code stays out of the
// initial bundle while the feature is dark.
const RedFlagRulesPage = lazy(() => import('./pages/RedFlagRulesPage'));
// NOT lazy, for the same reason as KpiPage above. Buddy Pool and the Sign-off
// form are no longer routed directly — they render as the two tabs of
// ShadowTrainingPage, which imports them.
import ShadowTrainingPage from './pages/ShadowTrainingPage';
// NOT lazy, for the same reason as KpiPage above: /governance/social-moderation
// is wrapped in <ErrorBoundary> without <Suspense>.
import SocialModerationPage from './pages/SocialModerationPage';
// NOT lazy, for the same reason: /governance/connect-content-policy is wrapped in
// <ErrorBoundary> without <Suspense>.
import ConnectContentPolicyPage from './pages/ConnectContentPolicyPage';
import TrainingSessionsPage from './pages/TrainingSessionsPage';
import LmsLeaderboardPage from './pages/LmsLeaderboardPage';
import LmsModulePreviewPage from './pages/lms/LmsModulePreviewPage';
import PushTestPage from './pages/PushTestPage';
import ManagerFocusDebugPage from './pages/ManagerFocusDebugPage';
import ErrorBoundary from './components/ErrorBoundary';
import SessionTimeoutDialog from './components/SessionTimeoutDialog';
import AnalyticsProvider from './components/AnalyticsProvider';
import { BlockLoader } from './components/loading';

// LMS Pages
import {
  LmsContentPage,
  LmsContentEditorPage,
  LmsModuleAssignmentsPage,
  LmsJourneysPage,
  LmsJourneyBuilderPage,
  LmsSessionsPage,
  LmsSessionFormPage,
  LmsSessionLearnersPage,
} from './pages/lms';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((state) => state.token);
  return token ? <>{children}</> : <Navigate to="/login" replace />;
}

// Configurable red-flag rules ship dark (RED_FLAG_RULES_ENABLED). Until the server says
// the feature is on, this URL renders nothing — exactly as before the page existed — and
// the page's code is never downloaded.
function RedFlagRulesRoute() {
  const enabled = useRedFlagRulesEnabled();
  if (!enabled) return null;
  return (
    <Suspense fallback={<BlockLoader label="Loading…" />}>
      <RedFlagRulesPage />
    </Suspense>
  );
}

// One element, under one key, for every dashboard address (built-in and custom). React
// keeps a component mounted when the same element type and key stay in the same place, so
// moving from one dashboard to another keeps the page and the data it has already loaded.
// A key that changed with the address would remount it and fetch everything again.
const dashboardElement = (
  <ErrorBoundary key="dashboards">
    <DashboardPage />
  </ErrorBoundary>
);

// The five section front pages share one element for the same reason: one page, told
// apart by its address.
const sectionFrontElement = (
  <ErrorBoundary key="section-front">
    <SectionFrontPage />
  </ErrorBoundary>
);

function App() {
  return (
    <Router>
      <AnalyticsProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
        {/* Public stats page — no auth required. Renders pool + cache health
            from /api/public/stats. Sits alongside /login outside the
            PrivateRoute wrapper. */}
        <Route path="/stats" element={<StatsPage />} />
        {/* The tab a Project Management launch link opens (Dashboard → Attrition →
            "Managed vs Unmanaged"). Signed-in only, but OUTSIDE the dashboard layout: it
            shows "Opening…" for a few seconds and then leaves for 1Office. */}
        <Route
          path="/open/project-management/:page"
          element={
            <PrivateRoute>
              <ErrorBoundary>
                <OpenProjectManagementPage />
              </ErrorBoundary>
            </PrivateRoute>
          }
        />
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <ErrorBoundary>
                <DashboardLayout>
                <Routes>
                  {/* Dashboard — the home screen, then one address per dashboard.
                      /dashboard/custom/:customId is declared first so it reads as the
                      exception to /dashboard/:slug (the router would rank it first anyway:
                      it is the longer, more literal path). An unknown slug is the
                      Dashboard page's to answer, not the page-not-found screen's. */}
                  <Route path="/dashboard" element={<ErrorBoundary key="home"><HomePage /></ErrorBoundary>} />
                  <Route path="/dashboard/custom/:customId" element={dashboardElement} />
                  <Route path="/dashboard/:slug" element={dashboardElement} />

                  {/* Search, second step: "Where do you want to look?" for a person or a
                      store picked in the search pop-up (?kind=person|store&id=). Who it is
                      about stays in the query, so the path is one fixed name. */}
                  <Route path="/search" element={<ErrorBoundary key="search"><SearchResultPage /></ErrorBoundary>} />

                  {/* Messages: the person's own chats and groups for the selected project.
                      No menu row leads here; the round link in the top bar does. The open
                      conversation stays in the query (?c= or ?g=), so the path is one
                      fixed name. The route is open to anyone signed in: the server
                      decides who has Messages, and the page says so when it refuses. */}
                  <Route path="/messages" element={<ErrorBoundary key="messages"><MessagesPage /></ErrorBoundary>} />

                  {/* Section front pages — every page the person holds in that section,
                      with one line each. These are the bare section addresses, which had
                      no route before (they showed a blank page); every page under them
                      keeps its own route below. My Approvals has no front page: it opens
                      straight on the list. */}
                  <Route path="/master-management" element={sectionFrontElement} />
                  <Route path="/reports" element={sectionFrontElement} />
                  <Route path="/governance" element={sectionFrontElement} />
                  <Route path="/grow" element={sectionFrontElement} />
                  <Route path="/settings" element={sectionFrontElement} />

                  {/* Master Management - New Routes */}
                  {/* Store Categories was a separate Master Data page before being
                      merged into Stores as a tab. Preserve the old bookmark,
                      landing on the right tab. */}
                  <Route path="/master-management/store-categories" element={<Navigate to="/master-management/stores?tab=categories" replace />} />
                  <Route path="/master-management/stores" element={<ErrorBoundary><StoreMasterPage /></ErrorBoundary>} />
                  <Route path="/master-management/employees" element={<ErrorBoundary><EmployeeMasterPage /></ErrorBoundary>} />
                  {/* Retired standalone route → the Custom Fields tab of the Employees page. */}
                  <Route path="/master-management/employee-custom-fields" element={<Navigate to="/master-management/employees?tab=custom-fields" replace />} />
                  <Route path="/master-management/products" element={<ErrorBoundary><ProductMasterPage /></ErrorBoundary>} />
                  <Route path="/master-management/sales-targets" element={<ErrorBoundary><SalesTargetsPage /></ErrorBoundary>} />
                  <Route path="/master-management/tasks" element={<TasksPage />} />
                  <Route path="/master-management/store-visit-forms" element={<ErrorBoundary><StoreVisitFormsPage /></ErrorBoundary>} />
                  <Route path="/master-management/market-regularization-reasons" element={<MarketRegularizationReasonsPage />} />
                  <Route path="/master-management/yellow-flag-reasons" element={<ErrorBoundary><YellowFlagReasonsPage /></ErrorBoundary>} />
                  <Route path="/master-management/grooming-prompt" element={<ErrorBoundary><GroomingPromptPage /></ErrorBoundary>} />
                  <Route path="/master-management/kpi" element={<ErrorBoundary><KpiPage /></ErrorBoundary>} />
                  <Route path="/master-management/red-flag-rules" element={<ErrorBoundary><RedFlagRulesRoute /></ErrorBoundary>} />
                  {/* Buddy Pool and Onboarding Sign-off were separate Master Data
                      pages before being merged into Grow → Onboarding Setup.
                      Preserve both old bookmarks, landing on the right tab. */}
                  <Route path="/master-management/buddy-pool" element={<Navigate to="/grow/shadow-training?tab=buddies" replace />} />
                  <Route path="/master-management/onboarding-signoff" element={<Navigate to="/grow/shadow-training?tab=form" replace />} />
                  {/* Targets & Achievements is now Manage → Targets inside Employee Score (Phase 3). */}
                  <Route path="/master-management/targets" element={<Navigate to="/reports/employee-score?tab=targets" replace />} />
                  {/* Offline Trainings now lives under Grow (was Master Management → Training Sessions). */}
                  <Route path="/grow/offline-trainings" element={<ErrorBoundary><TrainingSessionsPage /></ErrorBoundary>} />
                  {/* The mobile Grow tab's LMS points leaderboard, for the whole project. */}
                  <Route path="/grow/leaderboard" element={<ErrorBoundary><LmsLeaderboardPage /></ErrorBoundary>} />
                  {/* Shadow training SETUP — the buddy pool and the daily sign-off
                      form. Not client-side gated: only the server can evaluate the
                      'web.onboarding' grant, so the page renders for anyone and
                      turns the API's 403 into its access panel. A client admin
                      check here would lock out legitimate grant-holders who are
                      not project admins. */}
                  <Route path="/grow/shadow-training" element={<ErrorBoundary><ShadowTrainingPage /></ErrorBoundary>} />
                  {/* Preserve old bookmarks. */}
                  <Route path="/master-management/trainings" element={<Navigate to="/grow/offline-trainings" replace />} />
                  {/* Phase 3: the legacy Apple Scorecard, Employee Quadrants, and
                      Targets pages have been folded into /reports/employee-score
                      (View / Manage tabs). Old bookmarks land on the right sub-tab
                      via the `?tab=` query param the unified page honours. */}
                  <Route path="/reports/apple-scorecard" element={<Navigate to="/reports/employee-score?tab=score" replace />} />
                  {/* Quadrant is no longer a separate tab; chips above the Score table
                      now carry the distribution and let the user filter by quadrant. */}
                  <Route path="/reports/employee-quadrants" element={<Navigate to="/reports/employee-score?tab=score" replace />} />
                  <Route path="/reports/employee-score" element={<ErrorBoundary><EmployeeScorePage /></ErrorBoundary>} />
                  {/* Redirect the old master-management path as well. */}
                  <Route path="/master-management/apple-scorecard" element={<Navigate to="/reports/employee-score?tab=score" replace />} />
                  
                  {/* Reports - New Routes */}
                  <Route path="/reports/daily-attendance" element={<DailyAttendanceLogsPage />} />
                  <Route path="/reports/random-attendance" element={<RandomAttendanceLogsPage />} />
                  <Route
                    path="/reports/task-response"
                    element={
                      <Suspense fallback={<BlockLoader label="Loading…" />}>
                        <TaskResponseListPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/reports/task-response/:taskId"
                    element={
                      <Suspense fallback={<BlockLoader label="Loading…" />}>
                        <TaskResponseDetailPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/reports/task-response-v2"
                    element={<Navigate to="/reports/task-response" replace />}
                  />
                  <Route path="/reports/grooming" element={<GroomingReportPage />} />
                  <Route path="/reports/mood" element={<MoodReportPage />} />
                  <Route path="/reports/store-visits" element={<ErrorBoundary><StoreVisitsReportPage /></ErrorBoundary>} />
                  <Route path="/reports/store-visit-tasks" element={<ErrorBoundary><StoreVisitTasksReportPage /></ErrorBoundary>} />
                  <Route path="/reports/running-stock" element={<ErrorBoundary><RunningStockReportPage /></ErrorBoundary>} />
                  {/* Mobile Usage — PostHog-backed app usage for the selected project.
                      Deliberately NOT gated client-side: access is the per-role
                      `web.mobile_usage` grant, so the route renders for everyone and the
                      API's 403 becomes the AccessDeniedPanel inside the page. */}
                  <Route path="/reports/mobile-usage" element={<ErrorBoundary><MobileUsageReportPage /></ErrorBoundary>} />
                  <Route path="/reports/quiz-performance" element={<ErrorBoundary><QuizPerformanceReportPage /></ErrorBoundary>} />
                  <Route path="/reports/scorm-progress" element={<ErrorBoundary><ScormProgressReportPage /></ErrorBoundary>} />
                  <Route path="/reports/learning-journeys" element={<ErrorBoundary><LmsJourneyReportPage /></ErrorBoundary>} />
                  {/* Who has completed each content module, who is pending and who is overdue.
                      ?module=<id> opens it on one module's people. */}
                  <Route path="/reports/content-modules" element={<ErrorBoundary><LmsModuleReportPage /></ErrorBoundary>} />
                  <Route path="/reports/appeal-workflow" element={<ErrorBoundary><AppealWorkflowReportPage /></ErrorBoundary>} />
                  <Route path="/reports/yellow-flag-appeals" element={<ErrorBoundary><YellowFlagAppealsPage /></ErrorBoundary>} />
                  <Route path="/reports/yellow-flags" element={<ErrorBoundary><YellowFlagsReportPage /></ErrorBoundary>} />
                  <Route path="/reports/pip" element={<ErrorBoundary><PipPage /></ErrorBoundary>} />
                  
                  {/* Compliance & Governance — the menu row (code 'social_moderation')
                      is seeded by the backend's ensureSocialModerationMenu. */}
                  <Route path="/governance/social-moderation" element={<ErrorBoundary><SocialModerationPage /></ErrorBoundary>} />
                  {/* Connect content policy — the rules the AI reviews Connect posts,
                      edits and comments against (menu code 'connect_content_policy',
                      seeded by the 2026-09-25-connect-ai-review migration). Like Start
                      Exit, NOT client-gated: viewing needs moderator standing and
                      editing the `web.social_policy` page right (its page’s tick), both of which only
                      the server can evaluate, so the page turns a 403 into its access
                      panel and a read-only answer into a read-only editor. */}
                  <Route path="/governance/connect-content-policy" element={<ErrorBoundary><ConnectContentPolicyPage /></ErrorBoundary>} />
                  {/* Start Exit — the exit-review worklist (menu code 'start_exit',
                      seeded by the backend's ensureStartExitMenu). NOT admin-gated:
                      access is the `web.start_exit` page right — its page’s tick — (+ super admins),
                      which only the server can evaluate — so the route renders for
                      anyone and the page turns the API's 403 into its access panel.
                      Do not wrap this in a client-side admin check: it would lock out
                      legitimate grant-holders who are not project admins. The API
                      gates every route, so a stray nav grant still cannot act. */}
                  <Route path="/governance/start-exit" element={<ErrorBoundary><StartExitPage /></ErrorBoundary>} />

                  {/* My Approvals */}
                  <Route path="/approvals" element={<MyApprovalsPage />} />
                  {/* My Approvals → Probation Confirmation (menu code 'probation_confirmation').
                      Not client-side gated: the API resolves the signed-in user's 1Office
                      account and only ever lists / acts on their own approvals. */}
                  <Route path="/approvals/probation-confirmation" element={<ErrorBoundary><ProbationConfirmationPage /></ErrorBoundary>} />

                  {/* LMS */}
                  <Route path="/lms/content" element={<LmsContentPage />} />
                  <Route path="/lms/content/new" element={<LmsContentEditorPage />} />
                  <Route path="/lms/content/:id" element={<LmsContentEditorPage />} />
                  <Route path="/lms/content/:id/preview" element={<ErrorBoundary><LmsModulePreviewPage /></ErrorBoundary>} />
                  <Route path="/lms/content/:id/assignments" element={<LmsModuleAssignmentsPage />} />
                  {/* The module's learners were a page here before they became the
                      Reports → Module Completion report. Preserve the old address,
                      landing on that module. */}
                  <Route path="/lms/content/:id/learners" element={<LmsModuleLearnersRedirect />} />
                  <Route path="/lms/journeys" element={<LmsJourneysPage />} />
                  <Route path="/lms/journeys/new" element={<LmsJourneyBuilderPage />} />
                  <Route path="/lms/journeys/:id" element={<LmsJourneyBuilderPage />} />
                  <Route path="/lms/journeys/:id/assignments" element={<LmsModuleAssignmentsPage target="journey" />} />
                  <Route path="/lms/sessions" element={<LmsSessionsPage />} />
                  <Route path="/lms/sessions/new" element={<LmsSessionFormPage />} />
                  <Route path="/lms/sessions/:id" element={<LmsSessionFormPage />} />
                  <Route path="/lms/sessions/:id/assignments" element={<LmsModuleAssignmentsPage target="session" />} />
                  {/* Who a session was assigned to, and who registered and attended. */}
                  <Route path="/lms/sessions/:id/learners" element={<LmsSessionLearnersPage />} />

                  {/* Leave Policy */}
                  <Route path="/leave-policy" element={<LeavePolicyPage />} />
                  
                  {/* Settings */}
                  <Route path="/settings/access-management" element={<AccessManagementPage />} />
                  <Route path="/settings/user-roles" element={<UserRoleAssignmentPage />} />
                  <Route path="/settings/projects" element={<ErrorBoundary><ProjectManagementPage /></ErrorBoundary>} />
                  <Route path="/settings/push-test" element={<PushTestPage />} />
                  <Route path="/settings/focus-debug" element={<ErrorBoundary><ManagerFocusDebugPage /></ErrorBoundary>} />
                  <Route path="/settings/mailers" element={<ErrorBoundary><MailerSetupPage /></ErrorBoundary>} />
                  <Route path="/settings/distribution-lists" element={<ErrorBoundary><DistributionListsPage /></ErrorBoundary>} />
                  <Route path="/settings/roles" element={<Navigate to="/settings/access-management" replace />} />
                  <Route path="/settings/mobile-access" element={<Navigate to="/settings/access-management" replace />} />
                  <Route path="/settings/menus" element={<Navigate to="/settings/access-management" replace />} />
                  
                  {/* Redirects for Old Routes - Backward Compatibility */}
                  <Route path="/store-categories" element={<Navigate to="/master-management/stores?tab=categories" replace />} />
                  <Route path="/stores" element={<Navigate to="/master-management/stores" replace />} />
                  <Route path="/employee/master" element={<Navigate to="/master-management/employees" replace />} />
                  <Route path="/activities/task-management" element={<Navigate to="/master-management/tasks" replace />} />
                  <Route path="/activities/daily-attendance-logs" element={<Navigate to="/reports/daily-attendance" replace />} />
                  <Route path="/activities/random-attendance-logs" element={<Navigate to="/reports/random-attendance" replace />} />
                  <Route path="/activities/task-logs" element={<Navigate to="/reports/task-response" replace />} />
                  
                  {/* Default Route */}
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />

                  {/* Any address not listed above. It used to render a blank page under
                      the header; it now says the page was not found and offers the
                      Dashboard and the sections the person holds. The router ranks "*"
                      below every other path wherever it is written; it sits last so the
                      list reads the way it matches. */}
                  <Route path="*" element={<ErrorBoundary key="not-found"><NotFoundPage /></ErrorBoundary>} />
                </Routes>
              </DashboardLayout>
              </ErrorBoundary>
            </PrivateRoute>
          }
        />
        </Routes>
        <ErrorBoundary><SessionTimeoutDialog /></ErrorBoundary>
      </AnalyticsProvider>
    </Router>
  );
}

export default App;
